import { useEffect, useLayoutEffect, useRef } from 'react';

/** True if any part of the element is in/near the viewport (tall sections included). */
const isNearViewport = (element, marginPx = 80) => {
  const rect = element.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  return rect.bottom > -marginPx && rect.top < vh + marginPx;
};

export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
    /** Reveal immediately on mount (skip IntersectionObserver). */
    immediate = false,
  } = options;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (immediate || element.closest('.home')) {
      element.classList.add('animate');
    }
  }, [immediate]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (immediate || element.closest('.home')) return;

    const reveal = (target) => {
      target.classList.add('animate');
      if (triggerOnce) {
        observer.unobserve(target);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
          } else if (!triggerOnce) {
            entry.target.classList.remove('animate');
          }
        });
      },
      {
        // 0 = any visible pixel; avoids tall mobile sections never hitting 0.1/0.2
        threshold: Array.isArray(threshold) ? threshold : [0, threshold],
        rootMargin,
      }
    );

    observer.observe(element);

    // Layout/scroll-restoration can leave the first paint intersecting without an IO callback.
    const syncReveal = () => {
      if (!ref.current || ref.current.classList.contains('animate')) return;
      if (isNearViewport(ref.current)) {
        reveal(ref.current);
      }
    };

    syncReveal();
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(syncReveal);
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, immediate]);

  return ref;
};
